const STAGES = [
  {
    key: "buy",
    title: "Buy the property.",
    copy: "Start with a deal that fits your budget and investment criteria.",
    numbers: {
      origin: ["Purchase price", "$200,000", "The starting point\nfor this example"],
      payoff: ["Purchase loan", "$160,000", "80% of the purchase price"],
      returned: ["Down payment", "$40,000", "20% paid from your capital"],
      message: "Buy with the whole\nproject in mind.",
      summary: [
        ["Purchase price", "$200,000"],
        ["Purchase loan", "$160,000"],
        ["Down payment", "$40,000"],
      ],
      operator: "−",
      qualifier: "before costs",
      note: "Next: budget the renovation.",
    },
  },
  {
    key: "rehab",
    title: "Rehab with a plan.",
    copy: "Scope the work, budget repairs, and test your after-repair value.",
    numbers: {
      origin: ["Rehab budget", "$40,000", "Renovation paid\nfrom your capital"],
      payoff: ["Purchase + rehab", "$240,000", "Before closing and holding costs"],
      returned: ["After-repair value", "$320,000", "Assumed value after renovation"],
      message: "Plan the work.\nTest the upside.",
      summary: [
        ["Down payment", "$40,000"],
        ["Rehab budget", "$40,000"],
        ["Cash invested", "$80,000"],
      ],
      operator: "+",
      qualifier: "before costs",
      note: "Your capital funds the down payment and rehab.",
    },
  },
  {
    key: "rent",
    title: "Get rental-ready.",
    copy: "Check rental income against vacancy, operating costs, and financing.",
    numbers: {
      origin: ["Monthly rent", "$2,500", "Illustrative gross rent\n—not net cash flow"],
      payoff: ["Cash invested", "$80,000", "Down payment + renovation"],
      returned: ["Annual gross rent", "$30,000", "Assumes 12 occupied months"],
      message: "Check expenses\nbefore counting profit.",
      summary: [
        ["Monthly rent", "$2,500"],
        ["Months occupied", "12"],
        ["Annual gross rent", "$30,000"],
      ],
      operator: "×",
      qualifier: "before expenses",
      note: "Vacancy, operating costs, and loan payments reduce cash flow.",
    },
  },
  {
    key: "refinance",
    title: "Refinance the investment.",
    copy: "See what pays off the loan, comes back to you, and stays invested.",
    numbers: {
      origin: [
        "New refinance loan",
        "$240,000",
        "$320,000 after-repair value\n× 75% loan-to-value",
      ],
      payoff: ["Existing loan paid off", "$160,000", "Assumes the original balance"],
      returned: ["Cash returned to you", "$80,000", "Before refinance fees"],
      message: "See what comes back\nand what stays invested.",
      summary: [
        ["Initially invested", "$80,000"],
        ["Cash returned", "$80,000"],
        ["Still in the deal", "$0"],
      ],
      operator: "−",
      qualifier: "before fees",
      note: "100% of your original capital recovered.*",
    },
  },
  {
    key: "repeat",
    title: "Repeat with a plan.",
    copy: "Review the capital recovered before pursuing the next property.",
    numbers: {
      origin: ["Capital recovered", "$80,000", "From the refinance\nbefore fees"],
      payoff: ["Still in this property", "$0", "Of your original $80,000"],
      returned: ["Next-deal capital", "$80,000", "Before fees and cash reserves"],
      message: "Review your reserves.\nPlan your next purchase.",
      summary: [
        ["Initially invested", "$80,000"],
        ["Capital recovered", "$80,000"],
        ["Still in the deal", "$0"],
      ],
      operator: "−",
      qualifier: "before fees",
      note: "Recovered capital is not profit. Keep reserves for both properties.",
    },
  },
] as const;
/** Scroll owns the story. Short windows and reduced motion keep direct stage controls. */
export function initializeBrrrrMotion(document: Document, view: Window): () => void {
  const section = document.getElementById("brrrr");
  const panel = section?.querySelector<HTMLElement>(".brrrr-content");
  const caption = document.getElementById("brrrr-stage-caption");
  const title = document.getElementById("brrrr-stage-title");
  const description = document.getElementById("brrrr-stage-description");
  const cue = section?.querySelector<HTMLElement>(".brrrr-scroll-cue");
  const cueLabel = cue?.querySelector<HTMLElement>("[data-brrrr-scroll-label]");
  const buttons = Array.from(
    section?.querySelectorAll<HTMLButtonElement>("[data-brrrr-step]") ?? [],
  );
  if (!section || !panel || !caption || !title || !description || buttons.length !== STAGES.length)
    return () => {};

  const reduced = view.matchMedia?.("(prefers-reduced-motion: reduce)");
  const previousLive = caption.getAttribute("aria-live");
  const cleanup: Array<() => void> = [];
  const listen = (target: EventTarget, type: string, callback: EventListener): void => {
    target.addEventListener(type, callback, { passive: type === "scroll" });
    cleanup.push(() => target.removeEventListener(type, callback));
  };
  let selected = -1;
  let pinned = false;
  let stickyTop = 0;
  let travel = 0;
  let frame: number | undefined;
  let needsMeasure = false;
  let suspended = false;
  let disposed = false;
  let numbersVisible = false;
  let numberAnimations: Animation[] = [];
  const numberPanels = Array.from(
    section.querySelectorAll<HTMLElement>(
      ".refi-origin, .refi-payoff, .refi-return, .refi-capital-summary",
    ),
  );
  const setText = (selector: string, value: string): void => {
    const node = section.querySelector(selector);
    if (node) node.textContent = value;
  };
  const animateNumbers = (): void => {
    numberAnimations.forEach((animation) => animation.cancel());
    numberAnimations = [];
    if (disposed || reduced?.matches || !numbersVisible) return;
    numberPanels.forEach((panel, order) => {
      if (typeof panel.animate !== "function") return;
      numberAnimations.push(
        panel.animate(
          [
            { opacity: 0, transform: "translateY(10px)" },
            { opacity: 1, transform: "translateY(0)" },
          ],
          {
            duration: 380,
            delay: order * 45,
            easing: "cubic-bezier(0.22, 1, 0.36, 1)",
            fill: "backwards",
          },
        ),
      );
    });
  };

  const render = (index: number, manual = false): void => {
    if (selected === index && !manual) return;
    const stage = STAGES[index];
    if (!stage) return;
    const changed = selected !== index;
    selected = index;
    caption.setAttribute("aria-live", manual ? "polite" : "off");
    section.dataset.brrrrStage = stage.key;
    title.textContent = stage.title;
    description.textContent = stage.copy;
    const numbers = stage.numbers;
    for (const [selector, metric, detail] of [
      [".refi-origin", numbers.origin, "p"],
      [".refi-payoff", numbers.payoff, "small"],
      [".refi-return", numbers.returned, "small"],
    ] as const) {
      setText(`${selector} > span`, metric[0]);
      setText(`${selector} > strong`, metric[1]);
      setText(`${selector} > ${detail}`, metric[2]);
    }
    setText(".refi-return > p", numbers.message);
    const summary = section.querySelector(".refi-capital-summary");
    summary?.querySelectorAll(":scope > div").forEach((cell, index) => {
      const metric = numbers.summary[index];
      if (!metric) return;
      const label = cell.querySelector("span");
      const amount = cell.querySelector("strong");
      if (label) label.textContent = metric[0];
      if (amount) {
        amount.textContent = metric[1];
        if (index === 2) {
          const qualifier = document.createElement("small");
          qualifier.textContent = numbers.qualifier;
          amount.appendChild(qualifier);
        }
      }
    });
    setText(".refi-math-symbol", numbers.operator);
    setText(".refi-capital-summary > p", numbers.note);
    if (changed) animateNumbers();
    buttons.forEach((button, next) => {
      button.setAttribute("aria-pressed", String(next === index));
      button.dataset.complete = String(next < index);
    });
  };
  const measure = (): void => {
    const candidate =
      view.innerWidth >= 1100 &&
      !reduced?.matches &&
      typeof view.requestAnimationFrame === "function";
    section.classList.toggle("brrrr-pinned", candidate);
    const height = panel.getBoundingClientRect().height;
    pinned = candidate && height > 0 && height <= view.innerHeight - 48;
    section.classList.toggle("brrrr-pinned", pinned);
    if (pinned) {
      stickyTop = Math.max(24, Math.round((view.innerHeight - height) / 2));
      travel = STAGES.length * Math.min(340, Math.max(240, view.innerHeight * 0.36));
      section.style.setProperty("--brrrr-top", `${stickyTop}px`);
      section.style.setProperty("--brrrr-height", `${height + travel}px`);
    } else {
      section.style.removeProperty("--brrrr-top");
      section.style.removeProperty("--brrrr-height");
      section.style.removeProperty("--brrrr-progress");
    }
    if (cueLabel)
      cueLabel.textContent = pinned
        ? "Scroll to explore all five stages"
        : "Choose a stage to explore";
  };
  const sync = (): void => {
    if (!pinned) return;
    const progress = Math.max(
      0,
      Math.min(1, (stickyTop - section.getBoundingClientRect().top) / travel),
    );
    section.style.setProperty("--brrrr-progress", String(progress));
    render(Math.min(STAGES.length - 1, Math.floor(progress * STAGES.length)));
  };
  const schedule = (remeasure = false): void => {
    needsMeasure ||= remeasure;
    if (
      disposed ||
      suspended ||
      document.hidden ||
      frame !== undefined ||
      !view.requestAnimationFrame
    )
      return;
    frame = view.requestAnimationFrame(() => {
      frame = undefined;
      if (disposed) return;
      if (needsMeasure) measure();
      needsMeasure = false;
      sync();
    });
  };
  const cancelFrame = (): void => {
    if (frame !== undefined) view.cancelAnimationFrame(frame);
    frame = undefined;
  };

  buttons.forEach((button, index) => {
    listen(button, "click", () => {
      render(index, true);
      if (!pinned) return;
      const start = section.getBoundingClientRect().top + view.scrollY - stickyTop;
      // Put direct selections inside their reading interval, not on a rounding boundary.
      view.scrollTo({
        top: start + ((index + 0.15) / STAGES.length) * travel,
        behavior: "instant",
      });
    });
  });
  listen(view, "scroll", () => {
    if (pinned) schedule();
  });
  listen(view, "resize", () => schedule(true));
  if (reduced)
    listen(reduced, "change", () => {
      if (reduced.matches) {
        numberAnimations.forEach((animation) => animation.cancel());
        numberAnimations = [];
      }
      schedule(true);
    });
  listen(document, "visibilitychange", () => {
    if (document.hidden) cancelFrame();
    else schedule(true);
  });
  listen(view, "pagehide", () => {
    suspended = true;
    cancelFrame();
  });
  listen(view, "pageshow", () => {
    suspended = false;
    schedule(true);
  });
  const Resize = (view as Window & { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
  const observer = Resize ? new Resize(() => schedule(true)) : undefined;
  observer?.observe(panel);
  section.classList.add("is-brrrr-enhanced");
  if (cue) cue.hidden = false;
  render(view.innerWidth < 1100 ? 0 : 3);
  measure();
  sync();
  // Initialization usually happens above the fold, before Buy is visible.
  const Intersection = (view as Window & { IntersectionObserver?: typeof IntersectionObserver })
    .IntersectionObserver;
  const entrance = Intersection
    ? new Intersection(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          numbersVisible = true;
          animateNumbers();
          entrance?.disconnect();
        },
        { threshold: 0.05 },
      )
    : undefined;
  entrance?.observe(panel);

  return () => {
    disposed = true;
    numberAnimations.forEach((animation) => animation.cancel());
    numberAnimations = [];
    cancelFrame();
    observer?.disconnect();
    entrance?.disconnect();
    cleanup.forEach((remove) => remove());
    section.classList.remove("is-brrrr-enhanced", "brrrr-pinned");
    ["--brrrr-top", "--brrrr-height", "--brrrr-progress"].forEach((property) =>
      section.style.removeProperty(property),
    );
    render(3);
    buttons.forEach((button) => delete button.dataset.complete);
    if (previousLive === null) caption.removeAttribute("aria-live");
    else caption.setAttribute("aria-live", previousLive);
    if (cue) cue.hidden = true;
  };
}
